import { yupResolver } from "@hookform/resolvers/yup";
import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { english } from "flatpickr/dist/l10n/default";
import { Vietnamese } from "flatpickr/dist/l10n/vn";
import { convertDateTime, formatDateTime } from "helpers/utils";
import {
  useCheckRole,
  useExportStatisticalReport,
  useListTypeReport,
  useMyHubsList,
  useOrderStatus,
  useTicketStatus,
} from "hooks";
import React, { useEffect, useMemo, useState } from "react";
import Flatpickr from "react-flatpickr";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { Button, Card, CardBody, Col, FormGroup, Label, Row } from "reactstrap";
import * as yup from "yup";
import {
  initOptionDateLocale,
  buildReportYearDateRange,
  buildReportYearOptions,
  getDefaultReportYearOption,
  KPI_STATUS_OPTIONS,
  REPORT_TYPE_KPI_PERFORMANCE,
} from "./utils";
import { ORDER_TYPE_FILTER_OPTIONS } from "pages/Dashboard/OrderManagementDashboard/orderManagementDashboardUtils";

const StatisticalReportDocument = props => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["reportManagement"]);
  const [statusReportList, setStatusReportList] = useState([]);
  const [ticketStatusReportList, setTicketStatusReportList] = useState([]);
  const orderTypeReportList = [
    { value: null, label: "Tất cả" },
    ...ORDER_TYPE_FILTER_OPTIONS,
  ];
  const kpiStatusReportList = [
    { value: null, label: "Tất cả" },
    ...KPI_STATUS_OPTIONS,
  ];
  // const [is_level_2, setIsLevel2] = useState(true);
  const { isLogAdmin } = useCheckRole();
  const [isTicketReportType, setIsTicketReportType] = useState(false);

  const { data: myHubs, refetch: refetchMyHubs } = useMyHubsList();

  const reportYearOptions = useMemo(() => buildReportYearOptions(), []);

  const getInitDefaultValues = () => {
    return {
      statistical_type: undefined,
      time_report_date: [],
      report_year: getDefaultReportYearOption(),
      order_status_ids: [],
      order_type_ids: [],
      kpi_status_ids: [],
      ticket_status_ids: [],
      hub_id: [],
    };
  };

  const getInitControlFields = () => {
    return {
      display: {
        statistical_type: false,
        time_report_date: false,
        report_year: false,
        order_status_ids: false,
        order_type_ids: false,
        kpi_status_ids: false,
        ticket_status_ids: false,
        hub_id: false,
        btn_save: false,
      },
      disabled: {
        statistical_type: false,
        time_report_date: false,
        report_year: false,
        order_status_ids: false,
        order_type_ids: false,
        kpi_status_ids: false,
        ticket_status_ids: false,
        hub_id: false,
        btn_save: false,
      },
    };
  };

  const [controlFields, setControlFields] = useState(getInitControlFields);

  const [optionDateLocale, setOptionDateLocale] = useState({
    ...initOptionDateLocale,
  });

  const schema = yup.object().shape({
    statistical_type: yup
      .object()
      .required(
        t("form.validate.select", { title: t("form.statistical_type") })
      ),
    time_report_date: yup
      .array()
      .when("statistical_type", (statistical_type, yupNext) => {
        const typeCode = statistical_type?.code || "";
        switch (typeCode) {
          case "REPORT_TYPE_SYNTHESIS_ORDER":
          case "REPORT_TYPE_HISTORY_ORDER":
          case "REPORT_TYPE_TICKET":
            return yupNext.min(
              2,
              t("form.validate.select", {
                title: t("form.time_report_date"),
              })
            );
          default:
            break;
        }
        return yupNext;
      }),
    order_status_ids: yup
      .array()
      .when("statistical_type", (statistical_type, yupNext) => {
        const typeCode = statistical_type?.code || "";
        switch (typeCode) {
          case "REPORT_TYPE_SYNTHESIS_ORDER":
          case "REPORT_TYPE_HISTORY_ORDER":
            return yupNext.min(
              1,
              t("form.validate.select", {
                title: t("form.order_status_ids"),
              })
            );
          default:
            break;
        }
        return yupNext;
      }),
    ticket_status_ids: yup.array().when("statistical_type.code", {
      is: "REPORT_TYPE_TICKET",
      then: yup
        .array()
        .of(yup.object().shape({}))
        .min(
          1,
          t("form.validate.select", {
            title: t("form.ticket_status_ids"),
          })
        ),
      otherwise: yup.array().of(yup.object().shape({})),
    }),
    hub_id: yup.array().when("statistical_type.code", {
      is: value =>
        value === "REPORT_TYPE_SYNTHESIS_ORDER" ||
        value === REPORT_TYPE_KPI_PERFORMANCE,
      then: yup
        .array()
        .of(yup.object().shape({}))
        .min(
          1,
          t("form.validate.select", {
            title: t("form.hub_id"),
          })
        ),
      otherwise: yup.array().of(yup.object().shape({})),
    }),
    report_year: yup.object().when("statistical_type.code", {
      is: REPORT_TYPE_KPI_PERFORMANCE,
      then: yup
        .object()
        .nullable()
        .required(
          t("form.validate.select", {
            title: t("form.report_year"),
          })
        ),
      otherwise: yup.object().nullable(),
    }),
    order_type_ids: yup.array().when("statistical_type.code", {
      is: REPORT_TYPE_KPI_PERFORMANCE,
      then: yup
        .array()
        .of(yup.object().shape({}))
        .min(
          1,
          t("form.validate.select", {
            title: t("form.order_type_ids"),
          })
        ),
      otherwise: yup.array().of(yup.object().shape({})),
    }),
    kpi_status_ids: yup.array().when("statistical_type.code", {
      is: REPORT_TYPE_KPI_PERFORMANCE,
      then: yup
        .array()
        .of(yup.object().shape({}))
        .min(
          1,
          t("form.validate.select", {
            title: t("form.kpi_status_ids"),
          })
        ),
      otherwise: yup.array().of(yup.object().shape({})),
    }),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  // List Type Report
  const { data: typeReportList, refetch: fetchListTypeReport } =
    useListTypeReport({});

  // List Order Status
  const onFetchOrderStatusListSuccess = statusReportList => {
    setStatusReportList([
      { value: null, label: "Tất cả" },
      ...statusReportList,
    ]);
  };
  const onFetchTicketStatusListSuccess = ticketStatusList => {
    setTicketStatusReportList([
      { value: null, label: "Tất cả" },
      ...ticketStatusList,
    ]);
  };

  const { refetch: onFetchOrderStatusList, data: orderStatusList } =
    useOrderStatus(onFetchOrderStatusListSuccess);
  const { refetch: onFetchTicketStatusList } = useTicketStatus(
    onFetchTicketStatusListSuccess,
    () => {
      setTicketStatusReportList([]);
    }
  );

  // Export Statistical Report
  const onSuccess = data => {
    // if (!data.status) {
    // } else {
    navigate("/report-management/list");
    // }
  };
  const { isLoading, mutate: exportStatisticalReport } =
    useExportStatisticalReport(onSuccess);

  useEffect(() => {
    fetchListTypeReport();
    onFetchOrderStatusList();
    onFetchTicketStatusList();
    refetchMyHubs();
    setControlFields(prev => ({
      display: {
        ...prev.display,
        statistical_type: true,
        btn_save: true,
      },
      disabled: {
        ...prev.disabled,
      },
    }));
  }, []);

  // useEffect(() => {
  //   const newParams = {
  //     is_new_address: is_level_2 ? "N" : "O",
  //   };
  //   setHubParams(newParams);

  //   setTimeout(() => {
  //     refetchMyHubs();
  //   }, 100);
  // }, [is_level_2]);

  // const handleChangeIsLevel2 = value => {
  //   setIsLevel2(value);
  //   setHubParams({
  //     is_new_address: value ? "N" : "O",
  //   });
  //   setTimeout(() => {
  //     refetchMyHubs();
  //   }, 100);
  // };

  // Language Change
  useEffect(() => {
    const lang = i18n.language;
    switch (lang) {
      case "vn":
        setOptionDateLocale({
          ...optionDateLocale,
          locale: {
            ...Vietnamese,
            ...initOptionDateLocale.locale,
          },
        });
        break;
      case "en":
        setOptionDateLocale({
          ...optionDateLocale,
          locale: {
            ...english,
            ...initOptionDateLocale.locale,
          },
        });
        break;
      default:
        break;
    }
  }, [t]);

  const onSubmit = _values => {
    const data = getValues();
    const { statistical_type } = data;

    const orderStatusList = [];
    if (data.order_status_ids.length > 0) {
      data.order_status_ids.forEach(item => {
        orderStatusList.push(item.value);
      });
    }
    const ticketStatusList = [];
    if (data.ticket_status_ids.length > 0) {
      data.ticket_status_ids.forEach(item => {
        if (item?.value != null && item?.value !== "") {
          ticketStatusList.push(String(item.value));
        }
      });
    }
    const hub_id = data.hub_id
      .map(item => item?.value)
      .filter(value => value != null && value !== "")
      .map(value => String(value));

    const typeCode = statistical_type.code;
    let dataSubmit = {};
    switch (typeCode) {
      case REPORT_TYPE_KPI_PERFORMANCE: {
        const selectedYear = data.report_year?.value;
        const { from_date, to_date } = buildReportYearDateRange(selectedYear);

        const orderTypeList = [];
        if (data.order_type_ids.length > 0) {
          data.order_type_ids.forEach(item => {
            if (item?.value != null && item?.value !== "") {
              orderTypeList.push(String(item.value));
            }
          });
        }
        const order_type =
          orderTypeList.length > 0
            ? orderTypeList.join(",")
            : ORDER_TYPE_FILTER_OPTIONS.map(option => option.value).join(",");

        const kpiStatusList = [];
        if (data.kpi_status_ids.length > 0) {
          data.kpi_status_ids.forEach(item => {
            if (item?.value != null && item?.value !== "") {
              kpiStatusList.push(String(item.value));
            }
          });
        }
        const kpi_status =
          kpiStatusList.length > 0 ? kpiStatusList.join(",") : null;

        dataSubmit = {
          object_type: String(data.statistical_type.value),
          hub_id,
          conditions: {
            from_date,
            to_date,
            order_type,
            kpi_status,
          },
        };
        break;
      }
      case "REPORT_TYPE_SYNTHESIS_ORDER":
        dataSubmit = {
          object_type: String(data.statistical_type.value),
          conditions: {
            from_date: convertDateTime(
              data.time_report_date[0],
              formatDateTime.YYYY_MM_DD,
              "-"
            ),
            to_date: convertDateTime(
              data.time_report_date[1],
              formatDateTime.YYYY_MM_DD,
              "-"
            ),
            status: orderStatusList.length > 0 ? orderStatusList : null,
            shipping_type: null,
            transport_id: null,
            // is_new_address: is_level_2 ? "N" : "O",
          },
          hub_id: hub_id.map(value => String(value)),
        };
        break;
      case "REPORT_TYPE_TICKET":
        dataSubmit = {
          object_type: String(data.statistical_type.value),
          conditions: {
            from_date: convertDateTime(
              data.time_report_date[0],
              formatDateTime.YYYY_MM_DD,
              "-"
            ),
            to_date: convertDateTime(
              data.time_report_date[1],
              formatDateTime.YYYY_MM_DD,
              "-"
            ),
            status: ticketStatusList,
          },
          hub_id: hub_id.map(value => String(value)),
        };
        break;
      default:
        dataSubmit = {
          object_type: String(data.statistical_type.value),
          conditions: {
            from_date: convertDateTime(
              data.time_report_date[0],
              formatDateTime.YYYY_MM_DD,
              "-"
            ),
            to_date: convertDateTime(
              data.time_report_date[1],
              formatDateTime.YYYY_MM_DD,
              "-"
            ),
            status: orderStatusList.length > 0 ? orderStatusList : null,
            shipping_type: null,
            transport_id: null,
          },
        };
        break;
    }
    exportStatisticalReport({
      data: dataSubmit,
    });
  };

  const initStatisticTypeHandler = () => {
    const data = getValues();
    const { statistical_type } = data;

    reset({ ...getInitDefaultValues(), statistical_type: statistical_type });

    const typeCode = statistical_type.code || "";
    setIsTicketReportType(typeCode === "REPORT_TYPE_TICKET");
    switch (typeCode) {
      case "REPORT_TYPE_SYNTHESIS_ORDER":
        setControlFields({
          display: {
            ...getInitControlFields().display,
            statistical_type: true,
            time_report_date: true,
            order_status_ids: true,
            hub_id: true,
            btn_save: true,
          },
          disabled: {
            ...getInitControlFields().disabled,
          },
        });
        break;
      case "REPORT_TYPE_HISTORY_ORDER":
        setControlFields({
          display: {
            ...getInitControlFields().display,
            statistical_type: true,
            time_report_date: true,
            order_status_ids: true,
            btn_save: true,
          },
          disabled: {
            ...getInitControlFields().disabled,
          },
        });
        break;
      case "REPORT_TYPE_TICKET":
        setControlFields({
          display: {
            ...getInitControlFields().display,
            statistical_type: true,
            time_report_date: true,
            ticket_status_ids: true,
            hub_id: true,
            btn_save: true,
          },
          disabled: {
            ...getInitControlFields().disabled,
          },
        });
        break;
      case REPORT_TYPE_KPI_PERFORMANCE:
        setControlFields({
          display: {
            ...getInitControlFields().display,
            statistical_type: true,
            report_year: true,
            hub_id: true,
            order_type_ids: true,
            kpi_status_ids: true,
            btn_save: true,
          },
          disabled: {
            ...getInitControlFields().disabled,
          },
        });
        break;
      case "REPORT_TYPE_CUSTOMER_MISS_ADDRESS":
        setControlFields({
          display: {
            ...getInitControlFields().display,
            statistical_type: true,
            btn_save: true,
          },
          disabled: {
            ...getInitControlFields().disabled,
          },
        });
        break;
      default:
        setControlFields({
          display: {
            ...getInitControlFields().display,
            statistical_type: true,
            btn_save: true,
          },
          disabled: {
            ...getInitControlFields().disabled,
          },
        });
        break;
    }
  };

  const typeReportOptions = useMemo(() => {
    if (
      typeReportList &&
      typeReportList.items &&
      typeReportList.items.length > 0
    ) {
      const list = typeReportList.items.filter(item => {
        if (!isLogAdmin) {
          return [
            "REPORT_TYPE_SYNTHESIS_ORDER",
            "REPORT_TYPE_HISTORY_ORDER",
          ].includes(item.code);
        }

        return true;
      });
      return list.map(item => {
        return {
          label: item.name,
          value: item.id,
          code: item.code,
        };
      });
    }
    return [];
  }, [typeReportList, isLogAdmin]);

  const hubOptions = useMemo(() => {
    if (isTicketReportType) {
      const centerHub = myHubs?.hub;
      if (centerHub?.is_center === 1) {
        return [
          {
            label: centerHub?.name,
            value: centerHub?.id,
          },
        ];
      }
      return [];
    }

    if (myHubs) {
      const { hub, childs_hub } = myHubs;
      if (isLogAdmin) {
        return [
          { value: null, label: "Tất cả" },
          {
            label: hub?.name,
            value: hub?.id,
          },
          ...childs_hub?.map(item => ({
            label: item?.name,
            value: item?.id,
          })),
        ];
      }
      return [
        {
          label: hub?.name,
          value: hub?.id,
        },
      ];
    }

    return [];
  }, [isLogAdmin, isTicketReportType, myHubs]);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("title.statistic")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.statistic")}
            breadcrumbItems={[
              { name: t("title.list"), link: "/report-management/list" },
              { name: t("title.statistic") },
            ]}
          />

          <Row className="row-cols-1">
            <Col>
              <Card>
                <CardBody>
                  <Row className="row-cols-1">
                    {controlFields.display.statistical_type && (
                      <Col>
                        <FormGroup className="mb-3">
                          <Label htmlFor="statistical_type">
                            {t("form.statistical_type")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="statistical_type"
                            render={({ field }) => (
                              <Select
                                {...field}
                                isDisabled={
                                  controlFields.disabled.statistical_type
                                }
                                placeholder={t("placeholder.select", {
                                  title: t("form.statistical_type"),
                                })}
                                options={typeReportOptions}
                                onChange={data => {
                                  field.onChange(data);
                                  initStatisticTypeHandler();
                                }}
                                classNamePrefix="select2-selection"
                              />
                            )}
                          />
                          {errors.statistical_type && (
                            <MessageFormValidate
                              error={errors.statistical_type.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}
                  </Row>

                  <Row className="row-cols-1 row-cols-md-2">
                    {controlFields.display.time_report_date && (
                      <Col>
                        <FormGroup className="mb-3">
                          <Label htmlFor="time_report_date">
                            {t("form.time_report_date")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="time_report_date"
                            render={({ field }) => (
                              <Flatpickr
                                {...field}
                                disabled={
                                  controlFields.disabled.time_report_date
                                }
                                className="form-control d-block Flatpickr__Input"
                                placeholder="DD/MM/YYYY - DD/MM/YYYY"
                                options={{
                                  ...optionDateLocale,
                                  mode: "range",
                                }}
                                onClose={dObj => {
                                  // Check If Select Only One And Close => Set 2 Object Date
                                  if (dObj && dObj.length === 1) {
                                    setTimeout(() => {
                                      setValue(
                                        "time_report_date",
                                        [dObj[0], dObj[0]],
                                        { shouldValidate: true }
                                      );
                                    }, 150);
                                  }
                                }}
                              />
                            )}
                          />
                          {errors.time_report_date && (
                            <MessageFormValidate
                              error={errors.time_report_date.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}

                    {controlFields.display.report_year && (
                      <Col>
                        <FormGroup className="mb-3">
                          <Label htmlFor="report_year">
                            {t("form.report_year")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="report_year"
                            render={({ field }) => (
                              <Select
                                {...field}
                                isDisabled={controlFields.disabled.report_year}
                                placeholder={t("placeholder.select", {
                                  title: t("form.report_year"),
                                })}
                                options={reportYearOptions}
                                classNamePrefix="select2-selection"
                              />
                            )}
                          />
                          {errors.report_year && (
                            <MessageFormValidate
                              error={errors.report_year.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}

                    {controlFields.display.order_type_ids && (
                      <Col>
                        <FormGroup className="mb-3">
                          <Label htmlFor="order_type_ids">
                            {t("form.order_type_ids")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="order_type_ids"
                            render={({ field }) => (
                              <Select
                                {...field}
                                isMulti
                                isDisabled={controlFields.disabled.order_type_ids}
                                placeholder={t("placeholder.select", {
                                  title: t("form.order_type_ids"),
                                })}
                                options={orderTypeReportList}
                                classNamePrefix="select2-selection"
                                onChange={data => {
                                  const hasSelectAll = data.some(
                                    item => item.value == null
                                  );
                                  if (hasSelectAll) {
                                    const orderTypeSelected =
                                      orderTypeReportList.filter(
                                        item => item.value != null
                                      );
                                    setValue("order_type_ids", orderTypeSelected);
                                  } else {
                                    field.onChange(data);
                                  }
                                }}
                              />
                            )}
                          />
                          {errors.order_type_ids && (
                            <MessageFormValidate
                              error={errors.order_type_ids.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}

                    {controlFields.display.kpi_status_ids && (
                      <Col>
                        <FormGroup className="mb-3">
                          <Label htmlFor="kpi_status_ids">
                            {t("form.kpi_status_ids")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="kpi_status_ids"
                            render={({ field }) => (
                              <Select
                                {...field}
                                isMulti
                                isDisabled={controlFields.disabled.kpi_status_ids}
                                placeholder={t("placeholder.select", {
                                  title: t("form.kpi_status_ids"),
                                })}
                                options={kpiStatusReportList}
                                classNamePrefix="select2-selection"
                                onChange={data => {
                                  const hasSelectAll = data.some(
                                    item => item.value == null
                                  );
                                  if (hasSelectAll) {
                                    const kpiStatusSelected =
                                      kpiStatusReportList.filter(
                                        item => item.value != null
                                      );
                                    setValue("kpi_status_ids", kpiStatusSelected);
                                  } else {
                                    field.onChange(data);
                                  }
                                }}
                              />
                            )}
                          />
                          {errors.kpi_status_ids && (
                            <MessageFormValidate
                              error={errors.kpi_status_ids.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}

                    {controlFields.display.order_status_ids && (
                      <Col>
                        <FormGroup className="mb-3">
                          <Label htmlFor="order_status_ids">
                            {t("form.order_status_ids")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="order_status_ids"
                            render={({ field }) => (
                              <Select
                                {...field}
                                isMulti
                                placeholder={t("placeholder.select", {
                                  title: t("form.order_status_ids"),
                                })}
                                options={statusReportList}
                                classNamePrefix="select2-selection"
                                onChange={data => {
                                  const hasSelectAll = data.some(
                                    item => item.value == null
                                  );
                                  if (hasSelectAll) {
                                    const statusReportListSelected =
                                      statusReportList.filter(
                                        item => item.value != null
                                      );
                                    setValue(
                                      "order_status_ids",
                                      statusReportListSelected
                                    );
                                  } else {
                                    field.onChange(data);
                                  }
                                }}
                              />
                            )}
                          />
                          {errors.order_status_ids && (
                            <MessageFormValidate
                              error={errors.order_status_ids.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}

                    {controlFields.display.ticket_status_ids && (
                      <Col>
                        <FormGroup className="mb-3">
                          <Label htmlFor="ticket_status_ids">
                            {t("form.ticket_status_ids")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="ticket_status_ids"
                            render={({ field }) => (
                              <Select
                                {...field}
                                isMulti
                                placeholder={t("placeholder.select", {
                                  title: t("form.ticket_status_ids"),
                                })}
                                options={ticketStatusReportList}
                                classNamePrefix="select2-selection"
                                onChange={data => {
                                  const hasSelectAll = data.some(
                                    item => item.value == null
                                  );
                                  if (hasSelectAll) {
                                    const selected = ticketStatusReportList.filter(
                                      item => item.value != null
                                    );
                                    setValue("ticket_status_ids", selected);
                                  } else {
                                    field.onChange(data);
                                  }
                                }}
                              />
                            )}
                          />
                          {errors.ticket_status_ids && (
                            <MessageFormValidate
                              error={errors.ticket_status_ids.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}

                    {controlFields.display.hub_id && (
                      <Col>
                        {/* <Row className="mb-2">
                          <Col md={6}>
                            <FormGroup>
                              <Label
                                className="d-flex align-items-center gap-1"
                                style={{ cursor: "pointer" }}
                                htmlFor="is_level_2"
                                onClick={() =>
                                  handleChangeIsLevel2(!is_level_2)
                                }
                              >
                                <Switch
                                  checked={is_level_2}
                                  onChange={handleChangeIsLevel2}
                                />
                                Địa chỉ 2 cấp
                              </Label>
                            </FormGroup>
                          </Col>
                        </Row> */}

                        <FormGroup className="mb-3">
                          <Label htmlFor="hub_id">
                            {t("form.hub_id")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="hub_id"
                            render={({ field }) => (
                              <Select
                                {...field}
                                isMulti
                                placeholder={t("placeholder.select", {
                                  title: t("form.hub_id"),
                                })}
                                options={hubOptions}
                                classNamePrefix="select2-selection"
                                onChange={data => {
                                  const hasSelectAll = data.some(
                                    item => item.value == null
                                  );
                                  if (hasSelectAll) {
                                    const hubSelected = hubOptions.filter(
                                      item => item.value != null
                                    );
                                    setValue("hub_id", hubSelected);
                                  } else {
                                    field.onChange(data);
                                  }
                                }}
                              />
                            )}
                          />
                          {errors.hub_id && (
                            <MessageFormValidate
                              error={errors.hub_id.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}
                  </Row>
                  <Row className="justify-content-end">
                    <Col className="col-auto">
                      <Link
                        to="/report-management/list"
                        className="btn btn-danger btn-label"
                      >
                        <i className="fas fa-undo label-icon"></i>
                        {t("form.cancel")}
                      </Link>
                    </Col>
                    {controlFields.display.btn_save && (
                      <Col className="col-auto">
                        <Button
                          color="primary"
                          className="btn-label"
                          type="button"
                          onClick={() => {
                            handleSubmit(onSubmit)();
                          }}
                          disabled={
                            isLoading || controlFields.disabled.btn_save
                          }
                        >
                          <i className="far fa-save label-icon"></i>
                          {t("form.export_report")}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default StatisticalReportDocument;
